import {useAppSelector} from '@/hooks';
import {useEffect, useRef, useState, useMemo} from 'react';
import debounce from 'lodash/debounce';

export function useGetPagePreviewThumbUrl(pageHashedId: string, shouldFetch: boolean): string {
  const [src, setSrc] = useState('');
  const lastFetchedInfo = useRef<{lastModifiedTimestamp: Date | null; currentPosterTime: number | null}>({
    lastModifiedTimestamp: null,
    currentPosterTime: null,
  });
  const pageLastModified = useAppSelector((state) => {
    return state.posterEditor?.posterObject?.pages.pagesHashMap[pageHashedId]?.lastModified;
  });
  const isPosterPlaying = useAppSelector((state) => {
    return state.posterEditor.isPlaying;
  });
  const isTimelineExpanded = useAppSelector((state) => {
    return state.posterEditor.isBottomWebSeekbarExpanded;
  });
  const currentPosterTime = useAppSelector((state) => {
    return state.posterEditor.currentTime;
  });

  const debouncedFetchSrc = useMemo(() => {
    return debounce(
      async (pageHashedId: string) => {
        const src = await window.posterEditor?.whiteboard?.pages.pagesHashMap[pageHashedId]?.getThumbnailPreviewUrl();
        if (src) {
          setSrc(src);
        }
      },
      1000,
      {
        leading: true,
        maxWait: 5000,
      }
    );
  }, []);

  const generateThumbnailPreviewUrl = async (): Promise<void> => {
    if (!pageLastModified) {
      return;
    }

    await debouncedFetchSrc(pageHashedId);
    lastFetchedInfo.current = {
      lastModifiedTimestamp: pageLastModified,
      currentPosterTime: currentPosterTime,
    };
  };

  const shouldSkipPreviewDueToTimeOutOfRange = (
    pageStartTime: number,
    pageEndTime: number,
    pageHasMotion: boolean,
    lastModifiedTimestamp: Date | null,
    lastPosterTime: number | null
  ): boolean => {
    const isLastModifiedSame = lastModifiedTimestamp === pageLastModified;
    const isCurrentPosterTimeChanged = lastPosterTime !== currentPosterTime;
    const isCurrentPosterWithinPage = currentPosterTime >= pageStartTime && currentPosterTime <= pageEndTime;

    return isLastModifiedSame && isCurrentPosterTimeChanged && (!isCurrentPosterWithinPage || !pageHasMotion);
  };

  useEffect(() => {
    if (!shouldFetch || isPosterPlaying || !isTimelineExpanded || !pageLastModified) {
      return;
    }

    const page = window.posterEditor?.whiteboard?.pages.getPage(pageHashedId);
    if (!page) {
      return;
    }

    const pageStartTime = page.getStartTime();
    const pageEndTime = page.getEndTime();
    if (pageStartTime === undefined || pageEndTime === undefined) {
      return;
    }

    const pageHasMotionOrAnimatingItems = page.items.hasMotionOrAnimatingItems();

    if (
      shouldSkipPreviewDueToTimeOutOfRange(
        pageStartTime,
        pageEndTime,
        pageHasMotionOrAnimatingItems,
        lastFetchedInfo.current.lastModifiedTimestamp,
        lastFetchedInfo.current.currentPosterTime
      )
    ) {
      return;
    }

    void generateThumbnailPreviewUrl();

    return (): void => {
      debouncedFetchSrc.cancel();
    };
  }, [shouldFetch, pageHashedId, pageLastModified, isPosterPlaying, isTimelineExpanded, currentPosterTime, debouncedFetchSrc]);

  return src;
}
